<template>
  <div class="form-container">
    <div class="vcore_container pe_container">
      <transition
        name="fade"
        mode="out-in"
        @beforeLeave="beforeLeave"
        @enter="enter"
        @afterEnter="afterEnter"
      >
        <div class="vcore_row" v-if="!showFRPAdditionalInformation">
          <InfoBox :isSelfEnr="true" :providerName="this.selectedProvider.institutionName" :logoSrc="logo"/> 
          <div class="vcore_col--xs--12">
            <h2 class="form-title" tabindex="-1" v-focus="lastActivated">
              Welcome!
            </h2>
            <h2 class="form-title">
              Please fill out the following information.
            </h2>
            <div class="form-description">
              Please complete the form for the financially responsible party.
              All fields required unless marked optional.
            </div>
          </div>
          <div class="vcore_col--xs--12">
            <CustomForm
              :ref="activeForms[0]"
              :form="formData.basic"
              :value="formValues.basic"
              @input="handleCustomFormInput('basic', $event)"
              @valid-change="handleValidChange(activeForms[0], $event)"
              @errors-change="handleErrorsChange(activeForms[0], $event)"
            />
          </div>
          <div class="vcore_col--xs--12">
            <CustomForm
              :ref="activeForms[1]"
              :form="formData.physical"
              :value="formValues.physical"
              @input="handleCustomFormInput('physical', $event)"
              @valid-change="handleValidChange(activeForms[1], $event)"
              @errors-change="handleErrorsChange(activeForms[1], $event)"
            />
          </div>
          <div class="vcore_col--xs--12 address-options_wrapper">
            <label
              class="vcore_checkbox-group"
              id="physical-address-label"
              for="physical-mailing-address-checkbox"
            >
              <div class="vcore_checkbox">
                <input
                  id="physical-mailing-address-checkbox"
                  type="checkbox"
                  class="vcore_checkbox-input"
                  v-model="physicalMailingAddressCheckboxLocal"
                />
                <span class="vcore_checkbox-indicator"></span>
              </div>
              <span class="vcore_checkbox-label"
                >Mailing address is different than physical address</span
              >
            </label>
            <label
              class="vcore_checkbox-group"
              id="previous-address-label"
              for="previous-address-checkbox"
            >
              <div class="vcore_checkbox">
                <input
                  id="previous-address-checkbox"
                  type="checkbox"
                  class="vcore_checkbox-input"
                  v-model="previousAddressCheckboxLocal"
                />
                <span class="vcore_checkbox-indicator"></span>
              </div>
              <span class="vcore_checkbox-label"
                >Moved in the past two years</span
              >
            </label>
          </div>
        </div>
        <FRPAdditionalInformation ref="additionalInfo" v-else>
          <template v-slot:mailing>
            <CustomForm
              ref="mailingForm"
              :form="formData.mailing"
              :value="formValues.mailing"
              @input="handleCustomFormInput('mailing', $event)"
              @valid-change="handleValidChange('mailingForm', $event)"
              @errors-change="handleErrorsChange('mailingForm', $event)"
            />
          </template>

          <template v-slot:previous>
            <CustomForm
              ref="previousForm"
              :form="formData.previous"
              :value="formValues.previous"
              @input="handleCustomFormInput('previous', $event)"
              @valid-change="handleValidChange('previousForm', $event)"
              @errors-change="handleErrorsChange('previousForm', $event)"
            />
          </template>
        </FRPAdditionalInformation>
      </transition>
    </div>
    <MailingModal
      :recAddresses="currentRecAddress"
      :enteredAddress="currentEnteredAddress"
      :show="mailingModalQueue.length > 0"
      :numSteps="numSteps"
      :curStep="numSteps - mailingModalQueue.length"
      :addressType="mailingModalQueue[0]"
      @close="closeModal"
      @continue="continueModal"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import BasicInformationForm from "../../forms/BasicInformation.js";
import PhysicalAddressForm from "../../forms/PhysicalAddress.json";
import PreviousAddressForm from "../../forms/PreviousAddress.json";
import MailingAddressForm from "../../forms/MailingAddress.json";
import Form from "@Shared/mixins/Form";
import MailingModal from "@Shared/components/MailingModal";
import FRPAdditionalInformation from "./FRPAdditionalInformation";
import LogoDisplay from "../LogoDisplay";
import { KEYS, getCustomBrandingByKey } from "@Shared/utils/CustomBranding";
import { scrollTop } from "@Shared/utils/Scroll";
import InfoBox from "@Shared/components/InfoBox";

export default {
  name: "FRPInformation",
  mixins: [Form],
  components: {
    FRPAdditionalInformation,
    MailingModal,
    LogoDisplay,
    InfoBox
  },
  data() {
    return {
      formData: {
        basic: BasicInformationForm,
        physical: PhysicalAddressForm,
        previous: PreviousAddressForm,
        mailing: MailingAddressForm,
      },
      activeForms: ["basicForm", "physicalForm"],
      mailingModalQueue: [],
      numSteps: 1,
    };
  },
  computed: {
    ...mapGetters([
      "physicalMailingAddressCheckbox",
      "previousAddressCheckbox",
      "uneditedPhysicalMailingAddressCheckbox",
      "uneditedPreviousAddressCheckbox",
      "showFRPAdditionalInformation",
      "recAddresses",
      "address",
      "selectedProvider",
      "editFlag",
    ]),
    physicalMailingAddressCheckboxLocal: {
      get() {
        return this.physicalMailingAddressCheckbox;
      },
      set(value) {
        this.setPhysicalMailingAddressCheckbox(value);
      },
    },
    previousAddressCheckboxLocal: {
      get() {
        return this.previousAddressCheckbox;
      },
      set(value) {
        this.setPreviousAddressCheckbox(value);
      },
    },
    isFRPAdditionalInformationChecked() {
      return (
        this.physicalMailingAddressCheckboxLocal ||
        this.previousAddressCheckboxLocal
      );
    },
    computedAdditionalForms() {
      let forms = [];

      if (this.showFRPAdditionalInformation) {
        if (this.physicalMailingAddressCheckboxLocal) {
          forms.push("mailingForm");
        }

        if (this.previousAddressCheckboxLocal) {
          forms.push("previousForm");
        }
      } else {
        forms = ["basicForm", "physicalForm"];
      }

      return forms;
    },
    currentRecAddress() {
      return this.mailingModalQueue.length > 0
        ? this.recAddresses(this.mailingModalQueue[0])
        : [];
    },
    currentEnteredAddress() {
      return this.mailingModalQueue.length > 0
        ? this.address(this.mailingModalQueue[0])
        : {};
    },
    logo() {
      return getCustomBrandingByKey(
        KEYS.LOGO,
        this.selectedProvider.customBrandingList
      );
    },
  },
  watch: {
    computedAdditionalForms: {
      handler() {
        this.activeForms = this.computedAdditionalForms;
      },
      immediate: true,
    },
    showFRPAdditionalInformation() {
      scrollTop(0, 300);
    },
  },
  methods: {
    ...mapActions([
      "createTimer",
      "renewTimer",
      "setPhysicalMailingAddressCheckbox",
      "setPreviousAddressCheckbox",
      "validateAddress",
      "setFormLoading",
      "setRequestStatus",
      "decrementEnrollmentCurrentStepIndex",
    ]),
    next() {
      this.setRequestStatus({
        propertyName: "formNextLoading",
        status: true,
      });

      let promises = [];

      if (!this.showFRPAdditionalInformation) {
        promises.push(this.validateAddress("physical"));
      } else {
        if (this.physicalMailingAddressCheckbox) {
          promises.push(this.validateAddress("mailing"));
        }

        if (this.previousAddressCheckbox) {
          promises.push(this.validateAddress("previous"));
        }
      }

      Promise.all(promises)
        .then((results) => {
          this.mailingModalQueue = [...results.filter((x) => x !== "valid")];
          this.numSteps = this.mailingModalQueue.length;

          this.nextStep();
        })
        .catch(() => {
          this.$emit("major-error");
        })
        .finally(() => {
          this.setRequestStatus({
            propertyName: "formNextLoading",
            status: false,
          });
        });
    },
    previous() {
      if (this.showFRPAdditionalInformation) {
        this.$store.commit("SET_SHOW_FRP_ADDITIONAL_INFORMATION", false);
      } else {
        this.$emit("prev-step");
      }
    },
    beforeLeave(element) {
      this.prevHeight = getComputedStyle(element).height;
    },
    enter(element) {
      this.createTimer();
      const { height } = getComputedStyle(element);
      element.style.height = this.prevHeight;
      setTimeout(() => {
        element.style.height = height;
      });
    },
    afterEnter(element) {
      element.style.height = "auto";
    },
    closeModal() {
      this.renewTimer();
      this.decrementEnrollmentCurrentStepIndex();
      this.mailingModalQueue = [];
    },
    nextStep() {
      this.renewTimer();

      if (this.mailingModalQueue.length === 0) {
        if (this.editFlag) {
          // If user added one of checkboxes on the basic info page, go there. Otherwise go to confrim
          if (
            !this.showFRPAdditionalInformation &&
            ((this.physicalMailingAddressCheckbox == true &&
              this.uneditedPhysicalMailingAddressCheckbox == false) ||
              (this.previousAddressCheckbox == true &&
                this.uneditedPreviousAddressCheckbox == false))
          ) {
            this.$store.commit("SET_SHOW_FRP_ADDITIONAL_INFORMATION", true);
          } else {
            this.$emit("save-step");
          }
        } else {
          if (
            !this.showFRPAdditionalInformation &&
            this.isFRPAdditionalInformationChecked
          ) {
            this.$store.commit("SET_SHOW_FRP_ADDITIONAL_INFORMATION", true);
          } else {
            this.$emit("next-step");
          }
        }
      }
    },
    continueModal(address) {
      this.formValues = {
        ...this.formValues,
        [this.mailingModalQueue.shift()]: address,
      };

      this.nextStep();
    },
  },
};
</script>

<style lang="scss" scoped>
h2 {
  /*font-family: Poppins;*/
  /*font-weight: 600;*/
  /*line-height: 42px;*/
}

::v-deep h3 {
  font-size: 21px;
  line-height: 25px;
  font-family: "Poppins";
  color: #43454d;
  font-weight: 500;
}

.address-options_wrapper {
  margin-top: 20px;
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.5s;
  transition-property: height, opacity;
  transition-timing-function: ease;
  overflow: hidden;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}

::v-deep .field {
  margin-bottom: 23px;

  @media (max-width: $grid-breakpoint-xl) and (min-width: $grid-breakpoint-md) {
    margin-bottom: 30px;
  }
}

::v-deep .feedback-position {
  @media (max-width: $grid-breakpoint-sm) {
    position: relative;
  }
}
</style>
